<script lang="ts" setup>
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import type { Fancybox } from '@fancyapps/ui'
import type { CarouselSlide } from '@fancyapps/ui/dist/carousel/carousel'
import type { FancyboxOptions } from '@fancyapps/ui/dist/fancybox/fancybox'

import { useResponsiveScreenFacade } from '~/composables/responsive-screen-facade'
import ImageType from '~/enums/image-type'
import helpers from '~/helpers'

type EmblaCarouselSlide = {
  type: 'image' | 'video'
  url: string
}

type Video = {
  thumbnailUrl: string
  url: string
}

const props = defineProps<{
  galleriesData: any[]
  imagesData: any[]
  productName: string | null
  videos: Video[]
}>()

const imageTypesForGallery = [
  ImageType.Original,
  ImageType.Thumbnail480p,
  ImageType.Thumbnail360p,
  ImageType.Thumbnail240p
]
const imageTypesForSlider = [
  ImageType.Original,
  ImageType.Thumbnail480p,
  ImageType.Thumbnail360p,
  ImageType.Thumbnail240p
]

let fancybox: typeof Fancybox

const { isMd } = useResponsiveScreenFacade()

const carouselRef = ref()

const fancyboxSlides = computed(() => [
  ...imageUrls.value.map(
    (x: object): Partial<CarouselSlide> => ({
      src: helpers.getImageUrl(x, imageTypesForGallery),
      thumbSrc: helpers.getImageUrl(x, imageTypesForSlider),
      type: 'image'
    } as Partial<CarouselSlide>)
  ),
  ...props.videos.map(
    (x: Video): Partial<CarouselSlide> => ({
      src: x.url,
      thumbSrc: x.thumbnailUrl,
      type: 'html5video'
    } as Partial<CarouselSlide>)
  )
])
const imageUrls = computed(() => {
  const mainImageUrls = helpers.getImageUrls(props.imagesData)
  const imageUrls = []

  if (mainImageUrls) {
    imageUrls.push(mainImageUrls)
  }

  for (const galleryData of props.galleriesData) {
    const galleryImageUrls = helpers.getImageUrls(galleryData.relationships.images.data)

    if (galleryImageUrls) {
      imageUrls.push(galleryImageUrls)
    }
  }

  return imageUrls
})
const carouselSlides = computed(() => [
  ...imageUrls.value.map(
    (x: object): EmblaCarouselSlide => ({ type: 'image', url: helpers.getImageUrl(x, imageTypesForSlider) })
  ),
  ...props.videos.map((x: Video): EmblaCarouselSlide => ({ type: 'video', url: x.thumbnailUrl }))
])

function slideTo (i: number): void {
  carouselRef.value?.scrollTo(i)
}

function showGalleryModalWindow (startIndex: number, isVideo: boolean): void {
  const options: Partial<FancyboxOptions> = {
    Carousel: {
      infinite: false,
      Thumbs: { type: 'classic' },
      Toolbar: {
        display: {
          left: ['zoomIn', 'zoomOut'],
          middle: ['counter'],
          right: ['close']
        }
      },
      Video: { autoplay: isVideo }
    },
    idle: false,
    showClass: false,
    startIndex,
    hideClass: false
  }

  fancybox.show(fancyboxSlides.value, options)
}

onMounted(async (): Promise<void> => {
  const { Fancybox: fancyboxModule } = await import('@fancyapps/ui')
  fancybox = fancyboxModule
})
</script>

<template>
  <div class="relative">
    <div class="md:flex">
      <div class="hidden md:mr-3 md:flex md:w-10 md:flex-col">
        <a
          v-for="({ url }, i) in carouselSlides"
          :key="i"
          class="mb-3 size-10 rounded-lg border-[1.5px] border-transparent bg-[#f7f7f7] p-1 !no-underline"
          :class="{ '!border-main-green': carouselRef?.selectedIndex === i }"
          href="#"
          @click.prevent="slideTo(i)"
        >
          <img v-lazy="url" :alt="productName" class="size-full object-contain" loading="lazy">
        </a>
      </div>

      <div
        class="
          relative flex w-full select-none flex-col p-1
          md:w-[calc(100%-52px)]
        "
      >
        <Carousel
          ref="carouselRef"
          id="anyrecs_button_area"
          class="z-[1] [&_.embla-dots]:bottom-4 [&_.embla-dots]:left-2 [&_.embla-dots]:bg-[#f7f7f7]"
          :is-loop="true"
          :show-buttons="false"
          :show-dots="!isMd"
        >
          <div class="-ml-4 flex h-[360px] md:h-[400px]">
            <div
              v-for="({ type, url }, i) in carouselSlides"
              :key="i"
              class="h-full flex-[0_0_100%] cursor-pointer bg-transparent pl-4 last:!pl-0"
              :class="type"
              @click="showGalleryModalWindow(i, type === 'video')"
            >
              <img v-lazy="url" :alt="productName" class="size-full object-contain" loading="lazy">
            </div>
          </div>
        </Carousel>

        <div class="absolute left-0 top-0 size-full">
          <slot name="overlay" />
        </div>
      </div>
    </div>
  </div>
</template>
